import { CleaningServices, FeaturedPlayList, SwapHoriz } from '@mui/icons-material';
import { Divider, Menu as MuiMenu } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useClearDeviceCache, useDeleteDevice } from '~/api/devices';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { MoreAction, MoreActionLink, MoreDeleteAction } from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import type { MoreActionProps, MoreMenuProps } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { DeviceLayout__Device as Device } from '../../queries/layout.generated';
import { ReplaceDialog } from './ReplaceDialog';

export interface MenuProps {
  device: Device | null | undefined;
  moreMenuProps: MoreMenuProps<Device>;
  moreActionProps: MoreActionProps;
}

export const Menu = ({ device, moreMenuProps, moreActionProps }: MenuProps) => {
  const { t } = useTranslation();
  const { currentUser, currentNetwork } = useAppContext();
  const navigate = useNavigate();
  const [deleteDevice] = useDeleteDevice();
  const [clearDeviceCache] = useClearDeviceCache();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [confirmClearCache, confirmClearCacheProps] = useConfirmDialog();
  const [replaceOpen, setReplaceOpen] = useState(false);

  return (
    <>
      <MuiMenu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        {...moreMenuProps}
      >
        {(currentUser.admin || currentNetwork.canManage.value) && (
          <MoreActionLink
            Icon={FeaturedPlayList}
            onClick={moreActionProps.closeMenu}
            title={t('pages.devices.show.menu.manifest')}
            to={`/devices/${device?.id}/manifest.json`}
            target="_blank"
          />
        )}

        {currentUser.admin && device && (
          <MoreAction
            Icon={CleaningServices}
            closeMenu={moreActionProps.closeMenu}
            onClick={async () => {
              if (!(await confirmClearCache())) return;
              await clearDeviceCache({
                variables: { input: { deviceId: Number(device.id) } },
              });
            }}
            title={t('pages.devices.show.menu.clear_cache')}
          />
        )}

        {currentNetwork.canActivateDevices.value && (
          <MoreAction
            Icon={SwapHoriz}
            closeMenu={moreActionProps.closeMenu}
            onClick={() => setReplaceOpen(true)}
            title={t('pages.devices.show.menu.replace.menu_item')}
          />
        )}

        {/* Separate conditionals: MenuList arrow keys only reach direct children. */}
        {currentUser.admin && <Divider />}
        {currentUser.admin && (
          <MoreDeleteAction
            {...moreActionProps}
            onClick={async () => {
              assert(moreMenuProps.context != null, 'delete: no device context');
              if (!(await confirmDelete())) return;
              await deleteDevice({
                variables: { deviceId: moreMenuProps.context.id },
              });
              navigate('../..');
              moreMenuProps.onClose();
            }}
          />
        )}
      </MuiMenu>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.devices.show.menu.permanently_delete')}
        deleteConfirm
        prompt={
          <span>
            {t('pages.devices.show.menu.delete_device_prompt')}{' '}
            <b>{t('common.warnings.cannot_be_undone')}</b>
          </span>
        }
        title={t('pages.devices.show.menu.delete_device')}
      />

      <ConfirmDialog
        {...confirmClearCacheProps}
        confirm={t('pages.devices.bulk_actions.confirms.clear_cache.confirm')}
        prompt={t('pages.devices.bulk_actions.confirms.clear_cache.prompt')}
        title={t('pages.devices.bulk_actions.confirms.clear_cache.title')}
      />

      {device && (
        <ReplaceDialog
          deviceId={device.id}
          networkId={device.network.id}
          onClose={() => setReplaceOpen(false)}
          open={replaceOpen}
        />
      )}
    </>
  );
};
